import { useState } from "react";
import {
  batch,
  grossProfit,
  scaleLines,
  specTotals,
  type BatchTarget,
  type Ingredient,
  type Spec,
} from "@baros/core";
import { money, pct } from "./format.js";

interface Props {
  readonly spec: Spec;
  readonly ingredients: ReadonlyMap<string, Ingredient>;
  /** The venue's menu price, GST inclusive, if it has one. */
  readonly price: number | null;
}

/** Containers a bar actually fills, in millilitres. */
const CONTAINERS = [700, 1000, 2000, 3000, 5000] as const;

const litres = (ml: number) => (ml >= 1000 ? `${+(ml / 1000).toFixed(2)} L` : `${ml} mL`);
const thousands = (n: number) => n.toLocaleString("en-AU");

/**
 * The batching calculator and the serve-size scaler (#69), on a spec.
 *
 * Read-only by design: nothing here is saved, and the build it starts from
 * is the spec as stored. Amounts come out as a person pours them — whole
 * millilitres, and bottles plus the rest where the ingredient has a bottle
 * size — because "1,540 mL" read off a tablet at 4pm is where a batch goes
 * wrong, and "2 × 700 mL + 140 mL" is not.
 */
export function BatchTool({ spec, ingredients, price }: Props) {
  const [mode, setMode] = useState<"volume" | "serves">("volume");
  const [volumeMl, setVolumeMl] = useState<number>(1000);
  const [litresText, setLitresText] = useState("1");
  const [servesText, setServesText] = useState("20");
  const [withWater, setWithWater] = useState(false);

  const current = specTotals(spec, ingredients);
  const [serveText, setServeText] = useState(() => String(Math.round(current.volumeMl)));

  const hasWater = spec.lines.some((l) => ingredients.get(l.ingredientId)?.kind === "dilution");
  const target: BatchTarget =
    mode === "volume" ? { kind: "volume", ml: volumeMl } : { kind: "serves", count: Number(servesText) || 0 };
  // Cheap enough to work out on every keystroke; there is nothing to cache.
  const result = batch(spec.lines, ingredients, target, withWater);
  const batchCost = result.lines.reduce((sum, l) => sum + l.ml * (ingredients.get(l.ingredientId)?.costPerMl ?? 0), 0);

  const serveMl = Number(serveText);
  const factor = current.volumeMl > 0 && serveMl > 0 ? serveMl / current.volumeMl : 0;
  const scaled = scaleLines(spec.lines, factor);
  const next = specTotals({ lines: scaled, sundries: spec.sundries }, ingredients);
  const nameOf = (id: string) => ingredients.get(id)?.name ?? id;

  if (spec.lines.length === 0) {
    return <div className="msg">This spec has no build yet, so there is nothing to batch.</div>;
  }

  return (
    <div className="batch-tool">
      <section>
        <div className="label">Batch</div>
        <div className="row batch-modes">
          <button className="btn" aria-pressed={mode === "volume"} onClick={() => setMode("volume")}>Fill a container</button>
          <button className="btn" aria-pressed={mode === "serves"} onClick={() => setMode("serves")}>Make so many serves</button>
        </div>

        {mode === "volume" ? (
          <div className="row batch-target">
            {CONTAINERS.map((ml) => (
              <button
                key={ml}
                className="btn sm"
                aria-pressed={volumeMl === ml}
                onClick={() => {
                  setVolumeMl(ml);
                  setLitresText(String(ml / 1000));
                }}
              >
                {litres(ml)}
              </button>
            ))}
            <label className="row batch-input">
              <input
                className="num-input"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.1}
                aria-label="Batch size in litres"
                value={litresText}
                onChange={(e) => {
                  setLitresText(e.target.value);
                  setVolumeMl(Math.round((Number(e.target.value) || 0) * 1000));
                }}
              />
              <span className="label">litres</span>
            </label>
          </div>
        ) : (
          <label className="row batch-target batch-input">
            <input
              className="num-input"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              aria-label="Number of serves"
              value={servesText}
              onChange={(e) => setServesText(e.target.value)}
            />
            <span className="label">serves</span>
          </label>
        )}

        {hasWater && (
          <label className="row batch-water">
            <input type="checkbox" checked={withWater} onChange={(e) => setWithWater(e.target.checked)} />
            <span>Batch the water too — for a stirred drink bottled ready to pour</span>
          </label>
        )}

        <div className="table batch-table">
          <div className="thead">
            <span className="b-name">Pour</span>
            <span className="b-ml">mL</span>
            <span className="b-bottles">In bottles</span>
          </div>
          {result.lines.map((l) => (
            <div className="trow" key={l.ingredientId}>
              <span className="b-name">{l.water ? "Water (the dilution)" : nameOf(l.ingredientId)}</span>
              <span className="b-ml num">{thousands(l.ml)}</span>
              <span className="b-bottles num">
                {l.bottles > 0 && l.bottleMl
                  ? `${l.bottles} × ${thousands(l.bottleMl)} mL${l.remainderMl > 0 ? ` + ${thousands(l.remainderMl)} mL` : ""}`
                  : "—"}
              </span>
            </div>
          ))}
        </div>

        <div className="batch-sum">
          Makes <strong>{+result.serves.toFixed(1)}</strong> serve{result.serves === 1 ? "" : "s"} ·{" "}
          {litres(result.volumeMl)} · batch cost {money(batchCost)}
          {result.dilutionPerServeMl > 0 && (
            <div className="muted">
              Add {+result.dilutionPerServeMl.toFixed(1)} mL of dilution per serve at service — stirred or shaken
              over ice, as the spec says.
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="label">Serve size</div>
        <label className="row batch-input" style={{ marginTop: 8 }}>
          <input
            className="num-input"
            type="number"
            inputMode="numeric"
            min={0}
            step={5}
            aria-label="Serve size in millilitres"
            value={serveText}
            onChange={(e) => setServeText(e.target.value)}
          />
          <span className="label">mL in the glass · the spec is {Math.round(current.volumeMl)} mL</span>
        </label>

        <div className="table batch-table">
          {scaled.map((l, i) => (
            <div className="trow" key={`${l.ingredientId}-${i}`}>
              <span className="b-name">{nameOf(l.ingredientId)}</span>
              <span className="b-ml num">
                {l.unit === "dash" || l.unit === "drop"
                  ? `${+(l.qty ?? 1).toFixed(1)} ${l.unit}`
                  : `${+l.ml.toFixed(1)} mL`}
              </span>
            </div>
          ))}
        </div>

        <div className="batch-compare">
          <div><span className="label">Cost / serve</span><span className="num">{money(current.cost)} → <strong>{money(next.cost)}</strong></span></div>
          <div><span className="label">Std drinks</span><span className="num">{current.standardDrinks.toFixed(1)} → <strong>{next.standardDrinks.toFixed(1)}</strong></span></div>
          <div><span className="label">ABV</span><span className="num">{pct(next.abv)}</span></div>
          <div>
            <span className="label">{price ? `GP at ${money(price)}` : "GP"}</span>
            <span className="num">
              {price ? <>{pct(grossProfit(price, current.cost))} → <strong>{pct(grossProfit(price, next.cost))}</strong></> : "No venue price"}
            </span>
          </div>
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Every line scales together, so the ABV holds. The price stays the venue&rsquo;s; set a new one on the spec if the serve changes.
        </div>
      </section>
    </div>
  );
}
